/**
 * Copyright (c) 2026, CYBRA Corp. All rights reserved.
 */
import { Component, EventEmitter, Input, Output } from "@angular/core";
import { PvhAuditQuantityAndReasonCodes, PvhReasonCodeAndComment, PvhReasonCodeOption, PvhScanChamberAttemptLineDto } from "app/services/pvh.scan-chamber.service";

/** Declare $ to call bootstrap */
declare var $: any;

/**
 * Dialog component that allows the User to select a Reason Code Option and optionally enter
 * a comment to tie to a Scan Chamber attempt.
 */
@Component({
  selector: 'ef-pvh-audit-quantity-dialog',
    templateUrl: './pvh.scan-chamber.audit-quantity.dialog.component.html',
    styleUrls: [
      '../../../../../../assets/css/infrastructure.css'
    ]
  })
export class PvhScanChamberOverrideAuditQuantityDialog {

  /** Reason Code Options available to the User */
  @Input()
  availableReasonCodeOptions?: PvhReasonCodeOption[];

  /** Output used to pass the selected Reason Code and User Entered Comment to the parent */
  @Output()
  userEnteredValues = new EventEmitter<PvhAuditQuantityAndReasonCodes>();

  /** Holds the Line Item whose Audit Quantity / Reason Codes are being displayed / edited */
  lineItem?: PvhScanChamberAttemptLineDto;

  /** Holds the Audit Quantity set for the Line Item being displayed / edited */
  auditQuantity: number = 0;

  /** Holds the Reason Codes / Comments being displayed / edited */
  reasonCodesAndComments: PvhReasonCodeAndComment[] = [];


  /** Constructor */
  constructor() { }

  /**
   * Sets the Line Item whose Audit Quantity / Reason Codes are being displayed / edited.
   * Used when emitting the events. 
   */
  public setLineItem(lineItem: PvhScanChamberAttemptLineDto) {
    this.lineItem = lineItem;
  }

  /**
   * Sets the Audit Quantity to initially display for the Line Item.
   */
  public setAuditQuantity(auditQuantity: number): void {
    this.auditQuantity = auditQuantity;
  }

  /**
   * Sets the Reason Codes / Comments 
   */
  public setReasonCodesAndComments(reasonCodesAndComments?: PvhReasonCodeAndComment[]): void {
    while (this.reasonCodesAndComments.length > 0) {
      this.reasonCodesAndComments.pop();
    }

    if (reasonCodesAndComments && reasonCodesAndComments.length > 0) {
      const codes: PvhReasonCodeAndComment[] = [];
      for (let rc of reasonCodesAndComments) {
        const foundRc = this.availableReasonCodeOptions?.find(r => r.reason_code === rc.reasonCode?.reason_code);
        codes.push({ reasonCode: foundRc, comment: rc.comment });
      }
      this.reasonCodesAndComments.push(...codes);
    }

    if (this.reasonCodesAndComments.length == 0) {
      this.reasonCodesAndComments.push({ comment: '' } as PvhReasonCodeAndComment);
    }
  }


  /** 
   * Shows the Dialog  
   */
  public show(): void {
    $("#auditQuantityDialog").modal("show");
  }

  /**
   * Hides the Dialog
   */
  public hide(): void {
    $("#auditQuantityDialog").modal("hide");
  }

  /**
   * Called when the "Cancel" UI Button is clicked.
   * Cancels / Hides the Dialog
   */
  onCancelClicked(): void {
    this.hide();
  }

  getAvailableReasonCodesForIndex(index: number): PvhReasonCodeOption[] {
    const rv: PvhReasonCodeOption[] = [];
    if (this.availableReasonCodeOptions) {
      for (let option of this.availableReasonCodeOptions) {
        let valid = true;
        for (let i = 0; i < this.reasonCodesAndComments.length; i ++) {
          if (i != index) {
            const rc = this.reasonCodesAndComments[i];
            if (rc.reasonCode?.reason_code === option.reason_code) {
              valid = false;
            }
          }
        }
        if (valid){
          rv.push(option);
        }
      }
      return rv;
    }



    return rv;
  }

  onAddReasonCodeClicked(): void {
    this.reasonCodesAndComments.push({ comment: '' } as PvhReasonCodeAndComment);
  }

  onRemoveReasonCodeClicked(index: number): void {
    this.reasonCodesAndComments.splice(index, 1);
  }

  /**
   * Called when the User changes the selected Reason Code. 
   * Used to clear the comment if they are no longer allowed / required.
   */
  onReasonCodeChanged(): void {
    console.log(`onReasonCodeChanged()`);
  }

  onSetClicked(): void {
    // Verify that a Line Item has been set to the Dialog
    if (!this.lineItem) {
      return;
    }

    // Get the Audit Quantity from the UI
    const auditQuantity = this.auditQuantity;

    // Get the valid codes / comments from the UI
    const codes: PvhReasonCodeAndComment[] = [];
    for (let code of this.reasonCodesAndComments) {
      if (code.reasonCode) {
        const rc = code.reasonCode;
        const comment = code.comment && code.comment.length > 0? code.comment : undefined;
        codes.push({ reasonCode: rc, comment: comment } as PvhReasonCodeAndComment);
      }
    }

    // If Audit Quantity is less than 0, then it signals the User wants to remove it. 
    // Otherwise, at least one reason code / comment is required.
    if (auditQuantity > -1 && codes.length == 0) {
      return;
    }

    this.userEnteredValues.emit({ lineItem: this.lineItem, auditQuantity: auditQuantity, reasonCodesAndComments: codes } as PvhAuditQuantityAndReasonCodes);
  }

}
